import { Box, Skeleton } from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import React, { useMemo, useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import {
  getBarColour,
  getShortTimeValue,
  getZoneRange,
  PointerChartType,
} from "../../../../../store/helper";
import BreakdownBarChart from "../../../../dashboard/BreakdownBarChart";
import PointerChart from "../../../../dashboard/PointerChart";
import InlineBanner from "../../../../common/InlineBanner";
import SecondaryTabMenuPicker from "../../../../common/SecondaryTabMenuPicker";
import { formatClockTime } from "../../helpers/activitySelectors";
import { PNOE_STATS_ARROW } from "../../model/workoutEnums";
import { TrainingZoneObj, WorkoutAnalysisType } from "../../model/workoutPlanTypes";
import { useWorkoutPlan } from "../../store/useWorkoutPlan";
import BarCardTwoFactor from "./BarCardTwoFactor";
import HeartRateChart from "./HeartRateChart";

const TABS = [
  { label: "HR zones", key: 0 },
  { label: "PNOE stats", key: 1 },
];

/**
 * The mobile `WorkoutAnalysisScreen` for a recorded session, as the body of
 * the analysis drawer (`WorkoutAnalysisPanel` carries the session's name
 * and the close): the day with the Time / Duration tile, then two tabs — HR
 * zones (average heart rate pointer, the BPM series coloured by zone, time
 * per zone) and PNOE stats (fat / carbs and aerobic / anaerobic shares, the
 * bio-age, fat-burn and metabolism effects).
 */
const WorkoutAnalysisContent = () => {
  const theme: Theme = useTheme();
  const { analysis, activeAnalysis, activeAnalysisStatus, retryAnalysis } = useWorkoutPlan();
  const [tab, setTab] = useState(0);

  if (!analysis) return null;

  return (
    <>
      {activeAnalysisStatus === "error" && (
        <InlineBanner
          text="Couldn't load the workout analysis."
          backgroundColor={theme.colorTokens.D[10]}
          iconColor={theme.colorTokens.D[70]}
          action={{ label: "Retry", onClick: retryAnalysis }}
        />
      )}
      {activeAnalysisStatus === "empty" && (
        <InlineBanner
          text="Analysis not available."
          backgroundColor={theme.colorTokens.P[10]}
          iconColor={theme.colors_ds.PRIMARY_ICON_MAIN}
        />
      )}
      {(activeAnalysisStatus === "loading" || activeAnalysisStatus === "idle") && (
        <Box sx={styles.skeleton}>
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              variant="rounded"
              animation="pulse"
              sx={{ bgcolor: theme.colorTokens.N[30], height: "96px" }}
            />
          ))}
        </Box>
      )}
      {activeAnalysisStatus === "ready" && activeAnalysis && (
        <>
          <DetailsCard analysis={activeAnalysis} />
          <SecondaryTabMenuPicker
            tabs={TABS}
            selectedTab={tab}
            setSelectedTab={setTab}
            isFullWidth={false}
          />
          {tab === 0 ? (
            <HrZonesTab analysis={activeAnalysis} />
          ) : (
            <PnoeStatsTab analysis={activeAnalysis} />
          )}
        </>
      )}
    </>
  );
};

export default WorkoutAnalysisContent;

/**
 * Mobile `WorkoutAnalysisDetailsCard` without its day line: the details pane
 * behind the drawer already shows the day, so the drawer starts with the
 * Time and Duration tile.
 */
const DetailsCard = ({ analysis }: { analysis: WorkoutAnalysisType }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.detailsBlock}>
      <Box sx={styles.tile(theme)}>
        <Box>
          <Box sx={styles.tileValue(theme)}>
            {formatClockTime(analysis.start)} - {formatClockTime(analysis.end)}
          </Box>
          <Box sx={styles.tileLabel(theme)}>Time</Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Box sx={styles.tileValue(theme)}>
            {Math.floor(analysis.duration_in_minutes)}
            <Box component="span" sx={styles.tileUnit}>
              {" "}
              min
            </Box>
          </Box>
          <Box sx={styles.tileLabel(theme)}>Duration</Box>
        </Box>
      </Box>
    </Box>
  );
};

/** Mobile `HR_zonesTab`: average HR pointer, the BPM chart, time per zone. */
const HrZonesTab = ({ analysis }: { analysis: WorkoutAnalysisType }) => {
  const theme: Theme = useTheme();
  const zoneRows = useMemo(() => zoneBreakdown(analysis.training_zones), [analysis]);
  return (
    <>
      {/* Mobile rounds value and limits before drawing; the shared chart does not. */}
      <PointerChart
        title="Average Heart Rate (bpm)"
        lowLimit={Math.round(analysis.min_hr_bpm)}
        highLimit={Math.round(analysis.max_hr_bpm)}
        value={Math.round(analysis.avg_hr_bpm)}
        type={PointerChartType.HR_zonesTab}
        rangeMessage="Heart Rate range"
      />
      <HeartRateChart samples={analysis.hr_details} zones={analysis.training_zones} />
      <Box sx={styles.section}>
        <Box sx={styles.sectionTitle(theme)}>Zone Duration</Box>
        <Box sx={styles.zoneTable(theme)}>
          <BreakdownBarChart data={zoneRows} type="withDurationAndPercentage" />
        </Box>
      </Box>
    </>
  );
};

/**
 * Mobile `calculateZonePercentages` + the row shape `ActivityTab` hands the
 * breakdown chart: zones with time share the 100%, the rounding remainder
 * is taken off the last one.
 */
const zoneBreakdown = (zones: TrainingZoneObj[]) => {
  const withTime = zones.filter((zone) => zone.duration_in_minutes > 0);
  const total = withTime.reduce((sum, zone) => sum + zone.duration_in_minutes, 0);
  const shares = withTime.map((zone) => ({
    zone: zone.zone,
    percentage: total > 0 ? Math.round((zone.duration_in_minutes / total) * 100) : 0,
  }));
  const sum = shares.reduce((acc, share) => acc + share.percentage, 0);
  if (sum > 100 && shares.length > 0) {
    shares[shares.length - 1].percentage -= sum - 100;
  }
  return zones.map((zone) => ({
    index: zone.zone - 1,
    label: `Zone ${zone.zone} ${getZoneRange(zone.range, zone.zone)}`,
    value: getShortTimeValue(zone.duration_in_minutes),
    percentage: `${shares.find((share) => share.zone === zone.zone)?.percentage ?? 0}%`,
  }));
};

/** Mobile `PNOE_statsTab`: two split bars and the three effect cards. */
const PnoeStatsTab = ({ analysis }: { analysis: WorkoutAnalysisType }) => {
  const fat = Math.round(analysis.fuel_usage.fat_percentage);
  const aerobic = Math.round(analysis.fuel_usage.aerobic_percentage);
  const metrics = [
    { label: "Bio age", effect: analysis.bio_age_effect },
    { label: "Fat burn", effect: analysis.fat_burn_effect },
    { label: "Metabolism", effect: analysis.metabolic_rate_effect },
  ];
  return (
    <>
      <Box sx={styles.bars}>
        <BarCardTwoFactor label1="Fat" label2="Carbs" value1={fat} value2={100 - fat} />
        <BarCardTwoFactor
          label1="Aerobic"
          label2="Anaerobic"
          value1={aerobic}
          value2={100 - aerobic}
        />
      </Box>
      <Box sx={styles.metricCards}>
        {metrics.map((metric) => (
          <EffectCard key={metric.label} label={metric.label} effect={metric.effect} />
        ))}
      </Box>
    </>
  );
};

/** Mobile `MetricsSectionCardWithoutValues`: the label and an up / down arrow, or a dash. */
const EffectCard = ({ label, effect }: { label: string; effect: number }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.metricCard(theme)}>
      <Box sx={styles.metricLabel(theme)}>{label}</Box>
      {effect === PNOE_STATS_ARROW.increase ? (
        <ArrowUpward sx={{ fontSize: 20, color: theme.colors_ds.SUCCESS_ICON_MAIN }} />
      ) : effect === PNOE_STATS_ARROW.decrease ? (
        <ArrowDownward sx={{ fontSize: 20, color: theme.colors_ds.PRIMARY_ICON_MAIN }} />
      ) : (
        <Box sx={styles.metricDash(theme)}>-</Box>
      )}
    </Box>
  );
};

const styles = {
  detailsBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  tile: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  tileValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  tileUnit: {
    fontFamily: "var(--font-Regular)",
  },
  tileLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "18px",
    lineHeight: "24px",
  }),
  zoneTable: (theme: Theme) => ({
    padding: "16px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  bars: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  metricCards: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "8px",
  },
  metricCard: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    gap: "8px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  metricLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  metricDash: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    lineHeight: "20px",
  }),
  skeleton: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
};
